import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus } from 'lucide-react';

const PROVIDER_TYPES = [
  { value: 'primary_care', label: 'Primary Care Physician', icon: '\u{1F468}\u200D\u2695\uFE0F' },
  { value: 'specialist', label: 'Specialist', icon: '\u{1FA7A}' },
  { value: 'pharmacy', label: 'Pharmacy', icon: '\u{1F48A}' },
  { value: 'hospital', label: 'Hospital/Facility', icon: '\u{1F3E5}' },
  { value: 'therapist', label: 'Therapist/Counselor', icon: '\u{1F9E0}' },
  { value: 'dentist', label: 'Dentist', icon: '\u{1F9B7}' },
  { value: 'other', label: 'Other', icon: '\u2695\uFE0F' },
];

const AddProviderDialog = ({ open, onOpenChange, provider, setProvider, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button className="btn-primary" data-testid="add-provider-btn">
        <Plus className="w-4 h-4 mr-2" />
        Add Provider
      </Button>
    </DialogTrigger>
    <DialogContent className="surface-card max-w-2xl">
      <DialogHeader>
        <DialogTitle>Add Medical Provider</DialogTitle>
        <DialogDescription>Add a doctor, specialist, pharmacy, or healthcare facility</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="provider-type">Provider Type</Label>
            <Select value={provider.provider_type} onValueChange={(value) => setProvider({ ...provider, provider_type: value })}>
              <SelectTrigger data-testid="provider-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDER_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.icon} {type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="provider-name">Name *</Label>
            <Input id="provider-name" data-testid="provider-name-input" placeholder="Dr. Smith / ABC Pharmacy"
              value={provider.name} onChange={(e) => setProvider({ ...provider, name: e.target.value })} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="specialty">Specialty / Department</Label>
          <Input id="specialty" data-testid="specialty-input" placeholder="e.g., Cardiology, Internal Medicine"
            value={provider.specialty} onChange={(e) => setProvider({ ...provider, specialty: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" type="tel" data-testid="phone-input" placeholder="(555) 123-4567"
              value={provider.phone} onChange={(e) => setProvider({ ...provider, phone: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="email-input" placeholder="contact@provider.com"
              value={provider.email} onChange={(e) => setProvider({ ...provider, email: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Textarea id="address" data-testid="address-input" placeholder="Street address, city, state, zip"
            value={provider.address} onChange={(e) => setProvider({ ...provider, address: e.target.value })} className="resize-none" rows={2} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea id="notes" data-testid="notes-input" placeholder="Additional information, office hours, etc."
            value={provider.notes} onChange={(e) => setProvider({ ...provider, notes: e.target.value })} className="resize-none" rows={2} />
        </div>
        <Button type="submit" className="w-full btn-primary" data-testid="submit-provider-btn">Add Provider</Button>
      </form>
    </DialogContent>
  </Dialog>
);

export { PROVIDER_TYPES };
export default AddProviderDialog;
